import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, writeAuthToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AlertCircle, RefreshCcw } from "lucide-react";

/**
 * Échange le session_id (fragment URL) contre une session serveur.
 *
 * SÉCURITÉ ANTI-RÉUTILISATION :
 *   Le session_id Emergent est à usage unique et expire en ~30s.
 *   - On vide IMMÉDIATEMENT l'URL hash (history.replaceState) pour qu'un refresh
 *     ne re-tente pas avec un id déjà consommé (sinon 404 user_data_not_found en boucle).
 *   - On marque le session_id comme "vu" en sessionStorage pour bloquer toute
 *     tentative dupliquée même via back/forward.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState("Échange du jeton…");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      navigate("/login?error=missing_session_id", { replace: true });
      return;
    }
    const session_id = decodeURIComponent(match[1]);

    // ÉTAPE 1 : Nettoyer IMMÉDIATEMENT l'URL pour empêcher tout rejeu.
    try {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    } catch { /* noop */ }

    // ÉTAPE 2 : Vérifier qu'on n'a pas déjà consommé ce session_id (dans cet onglet).
    try {
      const consumed = sessionStorage.getItem("pitboard_consumed_session_id");
      if (consumed === session_id) {
        // Déjà tenté dans cet onglet — direct vers login propre
        navigate("/login?error=session_already_used", { replace: true });
        return;
      }
      sessionStorage.setItem("pitboard_consumed_session_id", session_id);
    } catch { /* noop */ }

    (async () => {
      try {
        // 3. Exchange session_id -> session_token (server sets httpOnly cookie + returns token)
        const { data } = await api.post("/auth/session", { session_id });

        // 4. FALLBACK : stocker le token dans 2 storages (localStorage + cookie JS)
        //    Résiste à Edge Tracking Prevention, Safari ITP, mode privé.
        try {
          if (data?.session_token) {
            writeAuthToken(data.session_token);
          }
          sessionStorage.removeItem("pitboard_consumed_session_id");
        } catch { /* storage indisponible */ }

        setStep("Chargement de votre espace…");
        await refresh();
        navigate("/", { replace: true, state: { user: data.user } });
      } catch (e) {
        const detail = e?.response?.data?.detail || e?.message || "Erreur inconnue";
        const code = e?.response?.status;
        const responseData = e?.response?.data;
        const isExpired = code === 401 && /expir|introuvable|invalide/i.test(String(detail));
        // Si on reçoit du HTML brut (page de parking, 502 ingress, etc.) → diag DNS
        const isHtmlResponse = typeof responseData === "string" && /<html|<!DOCTYPE/i.test(responseData);
        // Erreur réseau pure (DNS qui résout vers nulle part, ECONNREFUSED, timeout) — pas de response
        const isNetworkError = !code && /Network Error|ERR_NETWORK|timeout/i.test(String(detail));
        const isDnsLikely = code === 404 || code === 502 || code === 503 || isHtmlResponse || isNetworkError;
        console.error("[AuthCallback] Échec auth", { code, detail, error: e });
        setError({
          message: isDnsLikely
            ? `Impossible de joindre le serveur d'authentification (code ${code || "?"}). Le domaine de l'application est peut-être mal configuré.`
            : (typeof detail === "string" ? detail : "Échec de la connexion"),
          code,
          isExpired,
          isCookieIssue: code === 401 && !isExpired,
          isDnsLikely,
        });
      }
    })();
  }, [navigate, refresh]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-app p-6" data-testid="auth-error">
        <div className="card max-w-md w-full text-center p-8">
          <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: "rgba(225,6,0,0.12)", color: "#E10600" }}>
            <AlertCircle size={28} strokeWidth={2} />
          </div>
          <h2 className="font-display text-2xl mb-2" style={{ color: "var(--chalk)" }}>Connexion impossible</h2>
          <p className="text-sm text-[var(--ink-soft)] mb-4">
            {error.message}
          </p>
          {error.isDnsLikely && (
            <div className="text-left rounded-lg p-3 mb-4 text-[13px]" style={{ background: "rgba(225,6,0,0.10)", border: "1px solid rgba(225,6,0,0.30)" }}>
              <div className="font-semibold mb-1" style={{ color: "#E10600" }}>Cause probable : domaine mal configuré</div>
              <div style={{ color: "var(--chalk)" }}>
                L'URL <code className="font-mono text-[11px] px-1 py-0.5 rounded" style={{ background: "rgba(0,0,0,0.4)" }}>{window.location.host}</code> ne pointe pas vers le serveur PitBoard.
              </div>
              <ul className="list-disc list-inside mt-1.5 text-[var(--ink-soft)] space-y-1">
                <li>Vérifiez la configuration DNS de votre domaine</li>
                <li>Ou utilisez l'URL de preview Emergent</li>
                <li>Si vous venez de modifier le DNS, attendez 5-30 min de propagation</li>
              </ul>
            </div>
          )}
          {error.isExpired && (
            <div className="text-left rounded-lg p-3 mb-4 text-[13px]" style={{ background: "rgba(255,196,0,0.10)", border: "1px solid rgba(255,196,0,0.25)" }}>
              <div className="font-semibold mb-1" style={{ color: "#FFC400" }}>Pourquoi ?</div>
              <div style={{ color: "var(--chalk)" }}>
                Le lien Google que vous venez d'utiliser a expiré (valable ~30 secondes seulement).
                Cliquez "Recommencer" pour relancer une connexion fraîche.
              </div>
            </div>
          )}
          {error.isCookieIssue && (
            <div className="text-left rounded-lg p-3 mb-4 text-[13px]" style={{ background: "rgba(255,196,0,0.10)", border: "1px solid rgba(255,196,0,0.25)" }}>
              <div className="font-semibold mb-1" style={{ color: "#FFC400" }}>Cause probable :</div>
              <div style={{ color: "var(--chalk)" }}>Votre navigateur bloque les cookies / l'auth.</div>
              <ul className="list-disc list-inside mt-1.5 text-[var(--ink-soft)] space-y-1">
                <li>Désactivez le mode navigation privée stricte</li>
                <li>Sur iPhone : Réglages → Safari → Bloquer tous les cookies = <b>désactivé</b></li>
                <li>Essayez sur Chrome / Firefox</li>
              </ul>
            </div>
          )}
          <button onClick={() => { window.location.href = "/login"; }} className="btn btn-primary w-full" data-testid="auth-retry-btn">
            <RefreshCcw size={14} /> Recommencer la connexion
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-app">
      <div className="flex items-center gap-3 text-secondary-text">
        <div className="spinner" />
        <span>{step}</span>
      </div>
    </div>
  );
}
